import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Button,
  FullscreenExitButton,
  FullscreenDialogTitle,
  Select,
  Typography,
  Input,
  Checkbox,
} from 'sputnik-ui'
import {
  Fade,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
} from '@material-ui/core'
import { Help } from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { unwrapResult } from '@reduxjs/toolkit'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import debounce from 'lodash/debounce'
import { useDebouncedCallback } from 'use-debounce'
import i18next from 'i18next'
import styled, { css, keyframes, useTheme } from 'styled-components'
import ModalChangeDepthArchive from 'features/Entry/ModalChangeDepthArchive'
import { isBeward, isEltex } from 'utils/thirdPartyIntercomKinds'

import DeviceListItem from 'features/Devices/DeviceListItem'
import HighlightText from 'components/atoms/HighlightText'
import FormField from 'components/atoms/FormField'
import { showErrorMessage } from 'store/ui/actions'
import {
  fetchAvailable,
  setSearch,
  manualGateDevice,
  createInstallationPoint,
  linkDevice,
  importDeviceKeys,
} from 'features/Devices/installationsSlice'
import EltexKeysModal from 'features/Devices/EltexKeysModal'
import { ReactComponent as Pin } from '../AddAddressModal/pin.svg'
import AddressMap from './AddressMap'

function Transition(props) {
  return <Fade direction="up" {...props} />
}

const RUSSIA_NAMES = ['Россия', 'Russia']

// The geolocate pipeline (ss_gis's GeolocateAndCreateElements + Dadata
// reverse/forward geocoding) only has coverage for Russia. Non-Russia
// addresses fall back to the old direct linkDevice request the backend
// still supports unchanged.
const isRussianAddress = (values) => {
  return RUSSIA_NAMES.includes(values.country?.trim())
}

const createOption = (label, value, other = {}) => ({
  label,
  value,
  ...other,
})

const emptyAddress = {
  country: '',
  region: '',
  locality: '',
  street: '',
  house: '',
  entry: '',
  first_flat: '',
  last_flat: '',
  is_gate: false,
  lat: undefined,
  lon: undefined,
}

// first_flat/last_flat are only rendered for camera/intercom installs
// with an entry number set (the `isCameraOrIntercom && values.entry`
// guard around the flat range inputs below). requireFlatRange mirrors
// that guard so the schema only demands them when the fields are
// actually on screen.
const getAddressValidationSchema = (requireFlatRange) =>
  Yup.object()
    .shape({
      entry: Yup.number().max(
        100,
        i18next.t('common:field.error.max_entry', { number: 100 })
      ),
      first_flat: Yup.number()
        .min(1, i18next.t('common:field.error.greater_or_equal', { number: 1 }))
        .max(
          1000,
          i18next.t('common:field.error.greater_or_equal', { number: 1000 })
        )
        .when('entry', (entry, schema) =>
          requireFlatRange && entry
            ? schema.required(i18next.t('common:field.error.required'))
            : schema
        ),
      last_flat: Yup.number()
        .min(
          Yup.ref('first_flat'),
          i18next.t('common:field.error.invalid_range')
        )
        .max(
          1000,
          i18next.t('common:field.error.greater_or_equal', { number: 1000 })
        )
        .when('entry', (entry, schema) =>
          requireFlatRange && entry
            ? schema.required(i18next.t('common:field.error.required'))
            : schema
        ),
      country: Yup.string()
        .trim()
        .min(2, i18next.t('common:field.error.min_count', { number: 2 })),
      region: Yup.string()
        .trim()
        .min(2, i18next.t('common:field.error.min_count', { number: 2 })),
      locality: Yup.string()
        .trim()
        .min(2, i18next.t('common:field.error.min_count', { number: 2 })),
      street: Yup.string()
        .trim()
        .min(2, i18next.t('common:field.error.min_count', { number: 2 })),
      house: Yup.string()
        .trim()
        .min(1, i18next.t('common:field.error.min_house_num', { number: 1 })),
    })
    .test(
      'address-or-coordinates',
      i18next.t(
        'common:field.error.address_or_coordinates',
        'Fill in locality, street and house, or place a point on the map'
      ),
      (values) =>
        Boolean(
          (values.locality && values.street && values.house) ||
            (values.lat && values.lon)
        )
    )

// Address fields that can be filled by something other than typing (map
// drag, address search selection) get a brief glow so the user notices
// the auto-fill.
const AUTOFILL_FIELDS = [
  'country',
  'region',
  'locality',
  'street',
  'house',
  'entry',
]

const glowPulse = keyframes`
  from {
    box-shadow: 0 0 0 3px var(--glow-color);
  }
  to {
    box-shadow: 0 0 0 6px transparent;
  }
`

const GlowField = styled.div`
  border-radius: 4px;
  ${({ $glow }) =>
    $glow &&
    css`
      animation: ${glowPulse} 0.3s ease-out;
    `}
`

function AddAddressPageV2() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation(['devices', 'geo', 'common', 'messages'])

  const [state, setState] = useState({ identifier: '', ext_com: 0 })
  const [deviceType, setDeviceType] = useState('intercom')
  // Подсказка под выбором устройства: Beward стирает 3-значные коды, Eltex — без камеры и кодов.
  const [installHint, setInstallHint] = useState(null)
  const [keysModalOpen, setKeysModalOpen] = useState(false)
  const pendingInstall = useRef(null)

  const [searchText, setSearchText] = useState('')
  const [manualOpen, setManualOpen] = useState(false)
  const [fias, setFias] = useState({ locality_fias: '', street_fias: '' })
  const [geoResult, setGeoResult] = useState(null)
  const [geoError, setGeoError] = useState('')
  const [glowingFields, setGlowingFields] = useState({})
  const [changeDepthArchive, setChangeDepthArchive] = useState(false)
  const [responseAddress, setResponseAddress] = useState(null)
  const theme = useTheme()
  const glowTimeouts = useRef([])
  const modalArchiveDepthToggle = () => {
    setChangeDepthArchive(!changeDepthArchive)
  }

  useEffect(() => () => glowTimeouts.current.forEach(clearTimeout), [])

  const isCameraOrIntercom =
    state.identifier?.type === 'camera' || deviceType === 'intercom'

  const addressValidationSchema = useMemo(
    () => getAddressValidationSchema(isCameraOrIntercom),
    [isCameraOrIntercom]
  )

  const fiasLabel = (labelKey) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {t(labelKey)}
      <Tooltip title={t('devices:install_v2.fias_explain')}>
        <Help
          color="primary"
          style={{
            marginLeft: '.325rem',
            cursor: 'pointer',
            fontSize: 'inherit',
            height: 18,
            width: 18,
          }}
        />
      </Tooltip>
    </div>
  )

  const commutatorOptions = Array.from({ length: 7 }, (_, num) => ({
    value: num,
    label: t('devices:params.com_interval', {
      count: num,
      postProcess: 'interval',
    }),
  }))

  const availableDevices = useSelector(
    ({ installationsNew }) => installationsNew.available
  )
  const search = useSelector(({ installationsNew }) => installationsNew.search)
  const loadingAvailable = useSelector(
    ({ installationsNew }) => installationsNew.loadingAvailable
  )
  const linkPending = useSelector(
    ({ installationsNew }) => installationsNew.linkPending
  )
  const geolocatePending = useSelector(
    ({ installationsNew }) => installationsNew.geolocatePending
  )

  const [handleSearch] = useDebouncedCallback(
    (q) => dispatch(setSearch(q)),
    400
  )

  useEffect(() => {
    dispatch(fetchAvailable({ q: search }))
  }, [dispatch, search])

  // fixme
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLoadAddressOptions = useCallback(
    // fixme
    // eslint-disable-next-line consistent-return
    debounce((q, cb) => {
      if (!q || q.length < 3) return cb([])
      dispatch(manualGateDevice({ address: q, lookup: 'dadata' })).then(
        (action) => {
          if (manualGateDevice.fulfilled.match(action)) {
            cb(
              (action.payload || []).map((o) =>
                createOption(o.full_address, o.full_address, o)
              )
            )
          } else {
            cb([])
          }
        }
      )
    }, 350),
    []
  )

  const handleForwardGeocode = (values, setValues) => () => {
    const { country, region, locality, street, house } = values
    if (!locality || !street) return

    const text = [country, region, locality, street, house]
      .filter(Boolean)
      .join(', ')
    if (!RUSSIA_NAMES.includes(country)) return
    dispatch(manualGateDevice({ address: text, lookup: 'dadata' })).then(
      (action) => {
        if (manualGateDevice.fulfilled.match(action)) {
          const result = action.payload?.[0]
          if (result) {
            // lat/lon must land in the same render: setting them via two
            // separate setFieldValue calls (outside a React event handler,
            // so React 17 doesn't batch them) can hand the map a transient
            // { lat, lon: undefined } and crash Leaflet's LatLng parsing.
            setValues({ ...values, lat: result.lat, lon: result.lon })
          } else {
            dispatch(showErrorMessage(t('messages:search.empty')))
          }
        }
      }
    )
  }

  const redirectToNextPage = useCallback(() => {
    const { address_type, address_uuid } = responseAddress
    history.push(
      address_type === 'gate' || address_type === 'house'
        ? `/h/${address_uuid}`
        : `/e/${address_uuid}`
    )
  }, [history, responseAddress])

  const triggerGlow = (fields) => {
    if (!fields.length) return
    setGlowingFields((prev) => ({
      ...prev,
      ...Object.fromEntries(fields.map((f) => [f, true])),
    }))
    const timeoutId = setTimeout(() => {
      setGlowingFields((prev) => ({
        ...prev,
        ...Object.fromEntries(fields.map((f) => [f, false])),
      }))
    }, 300)
    glowTimeouts.current.push(timeoutId)
  }

  const handleAddressChange = (handleChange) => (e) => {
    handleChange(e)
    setGeoResult(null)
  }

  const handleAddressBlur = (handleBlur, values, setValues) => (e) => {
    handleBlur(e)
    handleForwardGeocode(values, setValues)()
  }

  const handleMapMove = (setValues, setFieldValue, values) => (lat, lon) => {
    setValues({ ...values, lat, lon })
    setGeoResult(null)

    dispatch(manualGateDevice({ lat, lon, lookup: 'dadata' })).then(
      (action) => {
        if (manualGateDevice.fulfilled.match(action)) {
          const result = action.payload?.[0]
          if (result) {
            const changedFields = AUTOFILL_FIELDS.filter((f) => result[f])
            changedFields.forEach((f) => setFieldValue(f, result[f]))
            triggerGlow(changedFields)
          }
        }
      }
    )
  }

  const handleCreateAndGeolocate = (values, { setValues: setFormValues }) => {
    setGeoError('')

    let address_type
    if (deviceType === 'gate') {
      address_type = 'gate'
    } else {
      address_type = values.entry ? 'entry' : 'house'
    }
    const params = {
      country_name: values.country,
      region_name: values.region,
      locality_name: values.locality,
      street_name: values.street,
      house_name: values.house,
      first_flat: values.first_flat,
      last_flat: values.last_flat,
      entry_name:
        !values.is_gate && isCameraOrIntercom
          ? values.entry || undefined
          : undefined,
      lat: values.lat,
      lon: values.lon,
      address_type,
    }

    return dispatch(createInstallationPoint(params)).then((action) => {
      if (createInstallationPoint.fulfilled.match(action)) {
        const result = action.payload
        setGeoResult(result)
        setFias({
          locality_fias: result.locality?.aoguid || '',
          street_fias: result.street?.aoguid || '',
        })

        // ss_gis returns the resolved/normalized address (country, region,
        // locality, street, house, entry) so we can fill the form with it
        // rather than just showing a confirmation summary.
        const resolved = {
          country: result.country?.name,
          region: result.region?.name,
          locality: result.locality?.name,
          street: result.street?.name,
          house: result.house?.name,
          entry: result.entry?.number,
        }
        const changedFields = Object.keys(resolved).filter(
          (field) => resolved[field]
        )

        if (changedFields.length) {
          const filledValues = { ...values }
          changedFields.forEach((field) => {
            filledValues[field] = resolved[field]
          })
          setFormValues(filledValues)
          triggerGlow(changedFields)
        }
      } else {
        setGeoError(action.payload)
      }
    })
  }

  const handleLinkDeviceSuccess = ({ address_uuid, address_type }) => {
    setResponseAddress({ address_uuid, address_type })
    // У контроллера Eltex нет камеры — архив предлагать нечего, сразу на адрес.
    if (isEltex(state.identifier?.model)) {
      history.push(
        address_type === 'gate' || address_type === 'house'
          ? `/h/${address_uuid}`
          : `/e/${address_uuid}`
      )
      return
    }
    setChangeDepthArchive(true)
  }

  // Ключи, залитые в контроллер Eltex напрямую. Адаптер приводит базу RFID контроллера
  // к списку ключей платформы, поэтому подъезд должен стать надмножеством устройства
  // (business_apps#1468). Калитки и дома вне MVP — там шаг пропускаем.
  const importEltexKeys = (addressUuid, addressType, keysFile) => {
    // Дом и калитка ключей не держат — переносить некуда.
    if (addressType !== 'entry') return Promise.resolve()

    return dispatch(
      importDeviceKeys({
        uuid: state.identifier?.value,
        entryUuid: addressUuid,
        file: keysFile,
      })
    ).then(unwrapResult)
  }

  // Ключи платформы живут на подъезде, поэтому шаг нужен только там. Номер подъезда
  // в форме необязателен: без него геокодер отдаёт address_type «house», переносить
  // ключи некуда — показываем предупреждение вместо обещания.
  const needsKeysStep = (values) =>
    isEltex(state.identifier?.model) &&
    deviceType !== 'gate' &&
    Boolean(values?.entry)

  const hintKey = (values) =>
    installHint === 'install_eltex_hint' && !needsKeysStep(values)
      ? 'install_eltex_no_keys_warn'
      : installHint

  const runInstall = (values, keysFile) => {
    if (!geoResult) return

    const identifier = state.identifier?.value
    const device_type =
      state.identifier?.type === 'camera' ? 'camera' : deviceType
    const { address_type, address_uuid } = geoResult

    const params =
      address_type === 'entry'
        ? {
            entry_uuid: address_uuid,
            identifier,
            device_type,
            first_flat: values.first_flat || undefined,
            last_flat: values.last_flat || undefined,
          }
        : address_type === 'gate'
        ? {
            house_uuid: geoResult.house.uuid,
            gate_uuid: address_uuid,
            identifier,
            device_type,
            address_type,
          }
        : { house_uuid: address_uuid, identifier, device_type, address_type }

    // Подъезд здесь уже нашёл геокодер, поэтому ключи пробуем добавить до привязки:
    // link_device сразу синхронизирует ключи подъезда с контроллером. Если геокодер
    // только что создал адрес, точки установки у него ещё нет — бэкенд отвечает
    // deferred, и импорт повторяем после привязки (ключей на таком подъезде нет,
    // синхронизация с устройства ничего не удалит).
    const keysImport = isEltex(state.identifier?.model)
      ? importEltexKeys(address_uuid, address_type, keysFile)
      : Promise.resolve(null)

    // Если импорт не удался, устройство не привязываем: иначе синхронизация
    // сотрёт с контроллера ключи, которых нет на подъезде. Ошибку показал thunk.
    keysImport
      .then((payload) =>
        dispatch(linkDevice(params))
          .then(unwrapResult)
          .then((linked) =>
            payload?.deferred
              ? importEltexKeys(address_uuid, address_type, keysFile).then(
                  () => linked
                )
              : linked
          )
          .then(handleLinkDeviceSuccess)
      )
      .catch(() => {})
  }

  const handleInstall = (values) => {
    if (needsKeysStep(values)) {
      pendingInstall.current = { values, mode: 'geo' }
      setKeysModalOpen(true)
      return
    }

    runInstall(values, null)
  }

  // Non-Russia addresses skip the geolocate pipeline entirely and go
  // straight to the legacy linkDevice request, matching what
  // AddAddressModal used to send.
  const runLegacyInstall = (values, keysFile) => {
    const isGate = deviceType === 'gate'

    const params = {
      ...values,
      entry:
        isCameraOrIntercom && !isGate ? values.entry || undefined : undefined,
      first_flat: values.first_flat || undefined,
      last_flat: values.last_flat || undefined,
      gate_lat: isGate ? values.lat : undefined,
      gate_lon: isGate ? values.lon : undefined,
      ext_com: state.ext_com,
      identifier: state.identifier?.value,
      device_type: state.identifier?.type === 'camera' ? 'camera' : deviceType,
    }

    return dispatch(linkDevice(params))
      .then(unwrapResult)
      .then((payload) => {
        // Здесь подъезд создаёт сама привязка: ключей на нём ещё нет, поэтому
        // синхронизация ничего не удаляет и импорт безопасен после link_device.
        const keysImport = isEltex(state.identifier?.model)
          ? importEltexKeys(
              payload.address_uuid,
              payload.address_type,
              keysFile
            )
          : Promise.resolve()

        // Ошибку импорта не проглатываем: ключи не перенесены, и уводить партнёра
        // на адрес нельзя — сообщение thunk'а исчезнет вместе с навигацией.
        return keysImport.then(() => handleLinkDeviceSuccess(payload))
      })
  }

  const handleSubmitAddress = (values, formikBag) => {
    if (isRussianAddress(values)) {
      handleCreateAndGeolocate(values, formikBag)
      return
    }

    if (needsKeysStep(values)) {
      pendingInstall.current = { values, mode: 'legacy' }
      setKeysModalOpen(true)
      return
    }

    runLegacyInstall(values, null)
  }

  const handleKeysConfirm = (keysFile) => {
    setKeysModalOpen(false)
    const pending = pendingInstall.current
    if (!pending) return

    if (pending.mode === 'geo') {
      runInstall(pending.values, keysFile)
    } else {
      runLegacyInstall(pending.values, keysFile)
    }
  }

  const handleClose = () => history.push('/')

  return (
    <>
      <Dialog
        open
        TransitionComponent={Transition}
        onClose={handleClose}
        scroll="paper"
        fullScreen
        transitionDuration={0}
      >
        <FullscreenExitButton onClick={handleClose} />
        <FullscreenDialogTitle>
          <Typography style={{ fontFamily: 'Play' }} variant="headline">
            {t('devices:install_v2.title')}
          </Typography>
        </FullscreenDialogTitle>

        <Formik
          initialValues={emptyAddress}
          validationSchema={addressValidationSchema}
          onSubmit={handleSubmitAddress}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            setFieldValue,
            setValues,
          }) => {
            const isRussia = isRussianAddress(values)
            const hasAddressOrCoords =
              (values.locality && values.street && values.house) ||
              (values.lat && values.lon)
            const hasErrors = Object.keys(errors).length > 0

            return (
              <Form style={styles.form}>
                <DialogContent style={styles.content}>
                  <Select
                    style={{ width: '100%' }}
                    label={t('devices:units.device', { count: 1 })}
                    variant="outlined"
                    handleSelect={(o) => {
                      setState({ ...state, identifier: o })
                      if (isBeward(o.model)) {
                        setInstallHint('install_beward_warn')
                      } else if (isEltex(o.model)) {
                        setInstallHint('install_eltex_hint')
                      } else {
                        setInstallHint(null)
                      }
                    }}
                    selected={state.identifier}
                    placeholder={t('devices:placeholder.search')}
                    isMulti={false}
                    isLoading={loadingAvailable}
                    options={availableDevices}
                    onInputChange={handleSearch}
                    notFoundMessage={t('messages:search.empty')}
                    // fixme
                    // eslint-disable-next-line react/no-unstable-nested-components
                    formatOptionLabel={(o) => (
                      <DeviceListItem
                        {...o}
                        type={o.type}
                        model={o.model}
                        secondaryLabel={t(
                          `devices:types.${
                            o.type === 'camera' ? 'camera' : deviceType
                          }`
                        )}
                      />
                    )}
                    filterOption={(o) => o}
                  />

                  {installHint && (
                    <DialogContentText
                      color={
                        hintKey(values) === 'install_eltex_hint'
                          ? 'textSecondary'
                          : 'error'
                      }
                      style={{ marginTop: '10px' }}
                    >
                      {t(`devices:helper_text.${hintKey(values)}`)}
                    </DialogContentText>
                  )}

                  {state.identifier?.type !== 'camera' && (
                    <Select
                      style={{ marginTop: '19px' }}
                      label={t('devices:params.com_type')}
                      variant="outlined"
                      isMulti={false}
                      handleSelect={(o) =>
                        setState({ ...state, ext_com: o.value })
                      }
                      options={commutatorOptions}
                      defaultValue={commutatorOptions[1]}
                    />
                  )}

                  {state.identifier?.type === 'intercom' && (
                    <div style={styles.deviceTypeToggle}>
                      <HighlightText
                        onClick={() => setDeviceType('intercom')}
                        active={deviceType === 'intercom'}
                      >
                        {t('devices:units.intercom')}
                      </HighlightText>
                      <HighlightText
                        onClick={() => setDeviceType('gate')}
                        active={deviceType === 'gate'}
                      >
                        {t('devices:units.gate')}
                      </HighlightText>
                    </div>
                  )}

                  <Typography variant="title" style={{ marginTop: 30 }}>
                    {t('devices:install_v2.search_address')}
                  </Typography>
                  <Select
                    async
                    variant="outlined"
                    loadOptions={handleLoadAddressOptions}
                    value={null}
                    inputValue={searchText}
                    onInputChange={(value, { action }) => {
                      if (action === 'input-change') setSearchText(value)
                    }}
                    handleSelect={(option) => {
                      setSearchText(option.full_address)
                      setValues({
                        ...values,
                        country: option.country || '',
                        region: option.region || '',
                        locality: option.locality || '',
                        street: option.street || '',
                        house: option.house || '',
                        lat: option.lat,
                        lon: option.lon,
                      })
                      setFias({ locality_fias: '', street_fias: '' })
                      setGeoResult(null)
                      setManualOpen(true)
                      triggerGlow(AUTOFILL_FIELDS.filter((f) => option[f]))
                    }}
                    placeholder={t('devices:install_v2.search_placeholder')}
                    isMulti={false}
                    style={{ width: '100%', marginTop: 12 }}
                    notFoundMessage={t('messages:search.empty')}
                  />

                  <Typography
                    color="primary"
                    style={styles.manualToggle}
                    onClick={() => setManualOpen((v) => !v)}
                  >
                    <Pin style={{ marginRight: 10, height: 24, width: 24 }} />
                    {t('devices:manual.enter_manual')}
                  </Typography>

                  {manualOpen && (
                    <>
                      <div
                        style={{
                          ...styles.manualGrid,
                          '--glow-color': theme.palette.primary[500],
                        }}
                      >
                        <GlowField $glow={glowingFields.country}>
                          <FormField
                            fullWidth
                            margin="dense"
                            label={t('devices:manual.country')}
                            name="country"
                            onChange={handleAddressChange(handleChange)}
                            onBlur={handleAddressBlur(
                              handleBlur,
                              values,
                              setValues
                            )}
                          />
                        </GlowField>
                        <GlowField $glow={glowingFields.region}>
                          <FormField
                            fullWidth
                            margin="dense"
                            label={t('devices:manual.region')}
                            name="region"
                            onChange={handleAddressChange(handleChange)}
                            onBlur={handleAddressBlur(
                              handleBlur,
                              values,
                              setValues
                            )}
                          />
                        </GlowField>
                        <div />

                        <div style={styles.fieldWithFias}>
                          <GlowField $glow={glowingFields.locality}>
                            <FormField
                              fullWidth
                              margin="dense"
                              label={t('devices:manual.locality')}
                              name="locality"
                              onChange={handleAddressChange(handleChange)}
                              onBlur={handleAddressBlur(
                                handleBlur,
                                values,
                                setValues
                              )}
                            />
                          </GlowField>
                          {isRussia && (
                            <Input
                              fullWidth
                              disabled
                              label={fiasLabel(
                                'devices:install_v2.fias_locality'
                              )}
                              value={fias.locality_fias}
                            />
                          )}
                        </div>
                        <div style={styles.fieldWithFias}>
                          <GlowField $glow={glowingFields.street}>
                            <FormField
                              fullWidth
                              margin="dense"
                              label={t('devices:manual.street')}
                              name="street"
                              onChange={handleAddressChange(handleChange)}
                              onBlur={handleAddressBlur(
                                handleBlur,
                                values,
                                setValues
                              )}
                            />
                          </GlowField>
                          {isRussia && (
                            <Input
                              fullWidth
                              disabled
                              label={fiasLabel(
                                'devices:install_v2.fias_street'
                              )}
                              value={fias.street_fias}
                            />
                          )}
                        </div>
                        <GlowField $glow={glowingFields.house}>
                          <FormField
                            fullWidth
                            margin="dense"
                            label={t('devices:manual.house')}
                            helperText={t(
                              'devices:helper_text.building_available'
                            )}
                            name="house"
                            onChange={handleAddressChange(handleChange)}
                            onBlur={handleAddressBlur(
                              handleBlur,
                              values,
                              setValues
                            )}
                          />
                        </GlowField>

                        {isCameraOrIntercom && (
                          <div style={styles.entryWithGateCheckbox}>
                            <GlowField $glow={glowingFields.entry}>
                              <FormField
                                fullWidth
                                margin="dense"
                                type="number"
                                label={t('devices:install_v2.entry')}
                                name="entry"
                                onChange={handleAddressChange(handleChange)}
                              />
                            </GlowField>
                          </div>
                        )}
                      </div>

                      {isCameraOrIntercom && values.entry && (
                        <div style={styles.flatRangeWrapper}>
                          <FormField
                            style={styles.flatRangeInput}
                            margin="dense"
                            type="number"
                            required={isCameraOrIntercom && values.entry}
                            label={t('devices:placeholder.first_flat')}
                            name="first_flat"
                          />
                          <FormField
                            style={styles.flatRangeInput}
                            margin="dense"
                            required={isCameraOrIntercom && values.entry}
                            type="number"
                            label={t('devices:placeholder.last_flat')}
                            name="last_flat"
                          />
                        </div>
                      )}

                      <div style={{ marginTop: 19 }}>
                        <AddressMap
                          lat={values.lat}
                          lon={values.lon}
                          onMove={handleMapMove(
                            setValues,
                            setFieldValue,
                            values
                          )}
                        />
                      </div>
                    </>
                  )}
                </DialogContent>

                {geoResult && (
                  <DialogContent style={styles.content}>
                    <Typography color="textSecondary" style={{ marginTop: 16 }}>
                      {t('devices:install_v2.confirm_address')}
                    </Typography>
                    <Typography style={{ marginTop: 6 }}>
                      {geoResult.full_address}
                    </Typography>
                  </DialogContent>
                )}

                <DialogActions style={styles.actions}>
                  <Button
                    style={{ fontFamily: 'Open Sans' }}
                    size="fullScreen"
                    variant="v2"
                    primary
                    rounded
                    type="submit"
                    disabled={
                      !state.identifier?.value ||
                      !hasAddressOrCoords ||
                      hasErrors ||
                      geolocatePending ||
                      linkPending
                    }
                  >
                    {isRussia
                      ? geolocatePending
                        ? t('devices:install_v2.geolocating')
                        : t('devices:install_v2.create_and_geolocate')
                      : linkPending
                      ? `${t('devices:actions.connect_device.connecting')}...`
                      : state.identifier?.type === 'camera'
                      ? t('devices:cameras.install')
                      : deviceType === 'gate'
                      ? t('devices:gates.install')
                      : t('devices:intercoms.install')}
                  </Button>

                  {isRussia && geoResult && (
                    <Button
                      style={{ fontFamily: 'Open Sans', marginLeft: '1rem' }}
                      size="fullScreen"
                      variant="v2"
                      primary
                      rounded
                      type="button"
                      onClick={() => handleInstall(values)}
                      disabled={
                        !state.identifier?.value || hasErrors || linkPending
                      }
                    >
                      {linkPending
                        ? `${t('devices:actions.connect_device.connecting')}...`
                        : state.identifier?.type === 'camera'
                        ? t('devices:cameras.install')
                        : deviceType === 'gate'
                        ? t('devices:gates.install')
                        : t('devices:intercoms.install')}
                    </Button>
                  )}
                </DialogActions>

                {geoError && (
                  <DialogContent style={styles.content}>
                    <Typography color="error">{geoError}</Typography>
                  </DialogContent>
                )}
              </Form>
            )
          }}
        </Formik>
      </Dialog>
      <EltexKeysModal
        open={keysModalOpen}
        uuid={state.identifier?.value}
        onClose={() => setKeysModalOpen(false)}
        onConfirm={handleKeysConfirm}
      />
      <ModalChangeDepthArchive
        selected={state.identifier?.value}
        isCamera={state.identifier?.type === 'camera'}
        open={changeDepthArchive}
        handleClose={modalArchiveDepthToggle}
        onSuccess={redirectToNextPage}
        beward={isBeward(state.identifier?.model)}
        allowChange
      />
    </>
  )
}

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
  content: {
    overflow: 'visible',
    maxWidth: 1275,
    margin: '0 auto auto',
    width: '100%',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1.325rem',
    marginBottom: 100,
    width: '100%',
  },
  deviceTypeToggle: {
    width: '100%',
    display: 'grid',
    gap: '1.625rem',
    gridTemplateColumns: 'min-content min-content',
    margin: '34px 0',
    justifyContent: 'center',
  },
  manualToggle: {
    display: 'flex',
    alignItems: 'center',
    color: '#0EA7FF',
    margin: '19px 10px',
    cursor: 'pointer',
  },
  manualGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridColumnGap: '0.8rem',
    gridRowGap: '0.8rem',
    marginTop: 19,
  },
  fieldWithFias: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
  },
  entryWithGateCheckbox: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '0.5rem',
    alignItems: 'center',
  },
  gateCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  flatRangeWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '1rem',
    marginTop: 19,
  },
  flatRangeInput: {
    minWidth: '160px',
  },
}

export default AddAddressPageV2
